import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { CORE_DEPOSIT_ACTION_TYPE } from "@obg/core.payment";
import { batchActions } from "@obg/ngrx-utils";
import {
	AppError,
	CustomerDetailSection,
	CustomerManagementAction,
	MessageScopeType,
	MessageService,
	MessageType,
	SESSION_ACTION_TYPE,
} from "@obg/ngx.core";
import { isEmpty } from "lodash-es";
import { merge, of } from "rxjs";
import { catchError, filter, map, switchMap } from "rxjs/operators";

import { CustomerAssessmentsAction } from "./customer-assessments.action";
import { CustomerAssessmentsConfigService } from "./customer-assessments.config";
import { CUSTOMER_ASSESSMENTS_MESSAGE_CODES, CUSTOMER_ASSESSMENTS_MESSAGE_CONTEXT } from "./customer-assessments.const";
import { CustomerAssessmentsDialogService } from "./customer-assessments.dialog";
import { AssessmentStatus, CustomerAssessments, CustomerAssessmentsResponse } from "./customer-assessments.model";
import { CustomerAssessmentsService } from "./customer-assessments.service";

@Injectable()
export class CustomerAssessmentsEffect {
	submitAnswers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerAssessmentsAction.submitAnswers),
			switchMap(({ model, assessmentId }) =>
				this.service.submitAnswers(model, assessmentId).pipe(
					map(() => CustomerAssessmentsAction.submitAnswersSuccess()),
					catchError((error: AppError) => of(CustomerAssessmentsAction.submitAnswersFail(error)))
				)
			)
		)
	);

	getAssessment$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerAssessmentsAction.getAssessment),
			switchMap(({ assessmentId }) =>
				this.service.getAssessment(assessmentId).pipe(
					map((model: CustomerAssessmentsResponse) => CustomerAssessmentsAction.getAssessmentSuccess(model)),
					catchError((error: AppError) => of(CustomerAssessmentsAction.getAssessmentFail(error)))
				)
			)
		)
	);

	getCustomerAssessments$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CustomerAssessmentsAction.getCustomerAssessments),
			filter(() => this.config.getIsCustomerAssessmentsEnabled()),
			switchMap(({ params }) =>
				this.service.getCustomerAssessments(params).pipe(
					map((assessments: CustomerAssessments[]) =>
						CustomerAssessmentsAction.getCustomerAssessmentsSuccess(assessments)
					),
					catchError((error: AppError) => of(CustomerAssessmentsAction.getCustomerAssessmentsFail(error)))
				)
			)
		)
	);

	getCustomerAssessmentsSuccess$ = createEffect(
		() =>
			this.actions$.pipe(
				ofType(CustomerAssessmentsAction.getCustomerAssessmentsSuccess),
				filter(({ assessments }) => !isEmpty(assessments)),
				switchMap(({ assessments }) =>
					this.customerAssessmentsDialogService.openSequentialAssessments(assessments)
				)
			),
		{ dispatch: false }
	);

	assessmentDepositTriggers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(CORE_DEPOSIT_ACTION_TYPE.completed),
			filter(() => this.config.getIsCustomerAssessmentsEnabled()),
			map(() =>
				batchActions(
					this.customerAction.getCustomerDetail(CustomerDetailSection.AllowedActions),
					CustomerAssessmentsAction.getCustomerAssessments({ status: AssessmentStatus.Requested })
				)
			)
		)
	);

	assessmentLoginTriggers$ = createEffect(() =>
		this.actions$.pipe(
			ofType(
				SESSION_ACTION_TYPE.loginSuccess,
				SESSION_ACTION_TYPE.loginWithTokenSuccess,
				SESSION_ACTION_TYPE.loginWithVerificationCodeSuccess,
				SESSION_ACTION_TYPE.loginWithPersonalNumberSuccess,
				SESSION_ACTION_TYPE.loginWithCookieSuccess
			),
			map(() => CustomerAssessmentsAction.getCustomerAssessments({ status: AssessmentStatus.Requested }))
		)
	);

	initMessages$ = createEffect(() =>
		merge(
			this.messageService.mapErrorByContext$({
				[CustomerAssessmentsAction.submitAnswersFail.type]: CUSTOMER_ASSESSMENTS_MESSAGE_CONTEXT.submitAnswers,
				[CustomerAssessmentsAction.getAssessmentFail.type]: CUSTOMER_ASSESSMENTS_MESSAGE_CONTEXT.getAssessment,
				[CustomerAssessmentsAction.getCustomerAssessmentsFail.type]:
					CUSTOMER_ASSESSMENTS_MESSAGE_CONTEXT.getCustomerAssessments,
			}),
			this.messageService.mapClearByContext$({
				[CustomerAssessmentsAction.submitAnswers.type]: CUSTOMER_ASSESSMENTS_MESSAGE_CONTEXT.submitAnswers,
			}),
			this.messageService.map$({
				[CustomerAssessmentsAction.submitAnswersSuccess.type]: {
					context: CUSTOMER_ASSESSMENTS_MESSAGE_CONTEXT.submitAnswers,
					scope: MessageScopeType.Local,
					type: MessageType.Success,
					messageCode: CUSTOMER_ASSESSMENTS_MESSAGE_CODES.submitAnswers.success,
				},
			})
		)
	);

	constructor(
		private actions$: Actions,
		private customerAssessmentsDialogService: CustomerAssessmentsDialogService,
		private service: CustomerAssessmentsService,
		private messageService: MessageService,
		private config: CustomerAssessmentsConfigService,
		private customerAction: CustomerManagementAction
	) {}
}
